import { DynamicPricingCore } from './dynamic-pricing-core.js';

const pricingErrorMessage = 'Please refresh page for price';

(function() {
    if (document.getElementById('dp-cart-styles')) return;
    const style = document.createElement('style');
    style.id = 'dp-cart-styles';
    style.textContent = `
        @keyframes dp-spin {
            to { transform: rotate(360deg); }
        }

        body.dp-cart-processing .totals__subtotal-value,
        body.dp-cart-processing .cart-item__prices .price,
        body.dp-cart-processing .lines-gt-price {
            color: transparent !important;
            position: relative;
            min-width: 60px;
            min-height: 1.2em;
            pointer-events: none;
        }

        body.dp-cart-processing .totals__subtotal-value *,
        body.dp-cart-processing .cart-item__prices .price *,
        body.dp-cart-processing .lines-gt-price * {
            visibility: hidden !important;
        }

        body.dp-cart-processing .totals__subtotal-value::after,
        body.dp-cart-processing .cart-item__prices .price::after,
        body.dp-cart-processing .lines-gt-price::after {
            content: '';
            position: absolute;
            top: 50%;
            left: 50%;
            width: 16px;
            height: 16px;
            margin: -8px 0 0 -8px;
            border: 2px solid #ccc;
            border-top-color: #333;
            border-radius: 50%;
            animation: dp-spin 0.6s linear infinite;
            visibility: visible !important;
        }

        body.dp-prices-ready .cart-item__prices .price,
        body.dp-prices-ready .lines-gt-price,
        body.dp-prices-ready .totals__subtotal-value {
            color: inherit !important;
            pointer-events: auto;
        }

        body.dp-prices-ready .cart-item__prices .price *,
        body.dp-prices-ready .lines-gt-price *,
        body.dp-prices-ready .totals__subtotal-value * {
            visibility: visible !important;
        }

        body.dp-prices-ready .totals__subtotal-value::after,
        body.dp-prices-ready .cart-item__prices .price::after,
        body.dp-prices-ready .lines-gt-price::after {
            content: none;
        }
    `;
    document.head.appendChild(style);
})();

export class DynamicPricingCart extends DynamicPricingCore {

    constructor(config) {

        super({
            ...config,
            cacheKey: config.cart?.cacheKey || 'cartPricingCache',
            api: config.api
        });

        this.cartApiConfig = {
            url: config.cart?.api_url || '/apps/proxy',
            pipe_id: config.cart?.pipe_id
        };

        if (!config.cart?.enabled) return;

        this.customerId = this.getCustomerId();
        this.variantSkuMap = new Map();
        this.cartTokenStorageKey = 'dynamicPricingLastCartToken';
        this.pendingSkuFetches = new Map();
        this.variantDataCache = new Map();
        this.isUpdatingCartUI = false;
        this.cartUIUpdateDebounceTimer = null;
        this.checkoutButtonsDisabled = true;
        this.pendingCartApiCall = null;
        this.suppressObserver = false;
        this.lastProcessedAt = 0;
        this.processingCooldownMs = 2000;
        this._cartCache = null;
        this._cartCacheTime = 0;
        this._cartCacheTTL = 1000;
        this._pendingReprocess = false;
        this._pendingReprocessCount = 0;
        this._maxPendingReprocesses = 2;
        this._lastSkuPriceMap = null;
        this._postCycleObserver = null;
        this._suppressUntil = 0;
        this._inflightAdds = 0;

        // ─── Retry Config ─────────────────────────────────────
        this.cartApiRetryEnabled = true;
        this.cartApiMaxRetries = 3;
        this.cartApiRetryDelayMs = 1000;

        this.init();
    }

    // ─── Page Detection ──────────────────────────────────────────────

    _detectPageType() {
        const path = window.location.pathname;
        if (path.includes('/cart')) return 'CART';
        if (path.includes('/products/')) return 'PDP';
        if (path.includes('/collections/')) return 'PLP';
        if (path.includes('/saved-carts')) return 'SAVED-CARTS';
        return 'OTHER: ' + path;
    }

    // ─── Initialization ──────────────────────────────────────────────

    init() {
        this.interceptFetchAddToCart();
        this.buildVariantSkuMap();

        if (document.referrer.includes('checkout') || document.referrer.includes('thank_you')) {
            this.reset();
        }

        this.checkForCartTokenChange();


        if (this.shouldRunCartPricing()) {
            this.processCartWithDynamicPricing(false, true);
        }

        this.setupEventListeners();
        this.setupCartPageObserver();
        this.setupDrawerOpenObserver();

        document.addEventListener('checkout:complete', () => this.reset());

        document.addEventListener('cart:clear', () => {
            this.reset();
            this.cleanupDynamicPricingUI();
        });

        document.addEventListener('page:load', () => {
            this.invalidateCartCache();
            this.checkForCartTokenChange();
            if (this.shouldRunCartPricing()) {
                this.processCartWithDynamicPricing(false, true);
            }
        });
    }

    // ─── Drawer Detection ────────────────────────────────────────────

    setupDrawerOpenObserver() {
        const findDrawer = () => {
            return document.querySelector('#CartDrawer, cart-drawer, [data-cart-drawer], .cart-drawer');
        };


        const attachAttrObserver = (drawerEl) => {
            let wasOpen = this._isDrawerOpen(drawerEl);

            const observer = new MutationObserver((mutations) => {
                for (const mutation of mutations) {
                    if (mutation.type !== 'attributes') continue;
                    const isNowOpen = this._isDrawerOpen(drawerEl);

                    if (isNowOpen && !wasOpen) {
                        wasOpen = isNowOpen;
                        // Disable immediately before any async work starts
                        this.disableCheckoutButtons();
                        if (!this.isUpdatingCartUI) {
                            this.invalidateCartCache();
                            this.startProcessing();
                            this.debouncedProcessCart(true);
                        }
                        return;
                    }

                    if (!isNowOpen && wasOpen) {
                        wasOpen = isNowOpen;
                        // Reset ready state so next open starts fresh
                        document.body.classList.remove('dp-prices-ready');
                    }
                }
            });

            observer.observe(drawerEl, {
                attributes: true,
                attributeFilter: ['open', 'aria-hidden', 'class', 'style']
            });
        };

        let drawer = findDrawer();

        if (drawer) {
            attachAttrObserver(drawer);
        } else {
            const bodyObserver = new MutationObserver(() => {
                if (!drawer) {
                    drawer = findDrawer();
                    if (drawer) {
                        attachAttrObserver(drawer);
                        bodyObserver.disconnect();
                    }
                }
            });
            bodyObserver.observe(document.body, { childList: true, subtree: true });
        }
    }

    _isDrawerOpen(drawerEl) {
        if (!drawerEl) return false;
        return drawerEl.hasAttribute('open') ||
            drawerEl.getAttribute('aria-hidden') === 'false' ||
            drawerEl.classList.contains('is-open') ||
            drawerEl.classList.contains('active') ||
            drawerEl.classList.contains('drawer--open') ||
            (drawerEl.style.display !== 'none' &&
                drawerEl.style.visibility !== 'hidden' &&
                drawerEl.offsetParent !== null);
    }

    // ─── Observer Suppression ────────────────────────────────────────

    _isSuppressed() {
        if (this.suppressObserver) return true;
        if (Date.now() < this._suppressUntil) return true;
        return false;
    }

    _setSuppression(durationMs) {
        this.suppressObserver = true;
        this._suppressUntil = Date.now() + durationMs;
        setTimeout(() => {
            this.suppressObserver = false;
        }, durationMs);
    }

    // ─── Cooldown Check ──────────────────────────────────────────────

    _isWithinCooldown() {
        return Date.now() - this.lastProcessedAt < this.processingCooldownMs;
    }

    // ─── Processing State (CSS-based spinners) ───────────────────────

    startProcessing() {
        document.body.classList.remove('dp-prices-ready');
        document.body.classList.add('dp-cart-processing');
    }

    finishProcessing() {
        document.body.classList.remove('dp-cart-processing');
        document.body.classList.add('dp-prices-ready');
    }

    // ─── Main Processing Cycle ───────────────────────────────────────

    async processCartWithDynamicPricing(force = false, isFirst = false) {
        if (this.isUpdatingCartUI) {
            if (force && this._pendingReprocessCount < this._maxPendingReprocesses) {
                this._pendingReprocess = true;
            }
            return;
        }

        // Wait for any in-flight add-to-cart requests to complete
        if (this._inflightAdds > 0) {
            await new Promise(resolve => {
                const check = () => {
                    if (this._inflightAdds <= 0) return resolve();
                    setTimeout(check, 50);
                };
                check();
            });
            this.invalidateCartCache();
        }

        const now = Date.now();
        if (!force && now - this.lastProcessedAt < this.processingCooldownMs) {
            return;
        }

        this.isFirst = isFirst;
        this.isUpdatingCartUI = true;
        this._pendingReprocess = false;
        this._setSuppression(3000);
        this.startProcessing();

        let cartWasEmpty = false;

        try {
            let cart = await this.getCart();

            if (!cart?.items?.length) {
                cartWasEmpty = true;
                this.cleanupDynamicPricingUI();
                return;
            }

            if (this.customerId && cart.token) {
                this.disableCheckoutButtons();
                let apiSuccess = false;
                try {
                    let skuPriceMap = {};

                    const maxAttempts = this.cartApiRetryEnabled ? this.cartApiMaxRetries : 1;

                    for (let attempt = 1; attempt <= maxAttempts; attempt++) {
                        // Log attempt if its not the first one
                        if (attempt !== 1) {
                            console.log(`[DP] API Retry ${attempt} / ${maxAttempts}`);
                        }

                        this.pendingCartApiCall = null; // Clear any stale pending call before retry
                        const apiResponse = await this.callCartPricingAPI(cart.token, this.customerId);
                        skuPriceMap = this.extractPricesFromApiResponse(apiResponse);

                        if (this._validateApiResponseOverlap(skuPriceMap, cart)) {
                            apiSuccess = true;
                            break;
                        }


                        if (attempt < maxAttempts) {
                            await new Promise(r => setTimeout(r, this.cartApiRetryDelayMs));
                            // Re-fetch cart in case it changed during the wait
                            this.invalidateCartCache();
                            cart = await this.getCart();
                            if (!cart?.items?.length) {
                                cartWasEmpty = true;
                                this.cleanupDynamicPricingUI();
                                return;
                            }
                        }
                    }

                    if (!apiSuccess) {
                        console.error(`[DP] All ${maxAttempts} retries exhausted`);
                    }

                    this._lastSkuPriceMap = skuPriceMap;

                    this.invalidateCartCache();
                    await this.triggerThemeCartRefresh();
                    await this.applyDynamicPricesAfterRefresh(skuPriceMap);
                } catch (error) {
                    console.error('DP Cart: API error, falling back to original prices', error);
                    // Show error in all line item price elements
                    document.querySelectorAll('.cart-item__prices .price .price__regular').forEach(el => {
                        el.innerHTML = `<span class="dp-error-message">${pricingErrorMessage}</span>`;
                    });

                    // Show error in subtotal
                    document.querySelectorAll('.totals__subtotal-value').forEach(el => {
                        el.innerHTML = `<span class="dp-error-message">${pricingErrorMessage}</span>`;
                    });

                    document.querySelectorAll('.price').forEach(el => {
                        el.innerHTML = `<span class="dp-error-message">${pricingErrorMessage}</span>`;
                    });

                    // Keep checkout disabled and show error message on button
                    document.querySelectorAll('[name="checkout"]').forEach(btn => {
                        btn.textContent = pricingErrorMessage;
                    });
                    this.checkoutButtonsDisabled = true;
                }
                if(apiSuccess) {
                    this.enableCheckoutButtons();
                }
            } else {
                this.enableCheckoutButtons();
            }
        } catch (error) {
            this.enableCheckoutButtons();
        } finally {
            this.isUpdatingCartUI = false;
            this.lastProcessedAt = Date.now();

            if (cartWasEmpty) {
                this._watchForPostEmptyRerenders();
            } else {

                if(this.isFirst === false && isFirst === true) {
                    // Should Skip
                } else {
                    this.finishProcessing();
                }

                this._watchForPostCycleRerenders();
            }

            if (this._pendingReprocess) {
                this._pendingReprocess = false;
                this._pendingReprocessCount++;
                this.invalidateCartCache();
                this.debouncedProcessCart(true);
            } else {
                this._pendingReprocessCount = 0;
            }
        }
    }

    // ─── Post-Cycle Re-render Watcher ────────────────────────────────

    _watchForPostCycleRerenders() {
        if (this._postCycleObserver) {
            this._postCycleObserver.disconnect();
            this._postCycleObserver = null;
        }

        if (!this._lastSkuPriceMap || Object.keys(this._lastSkuPriceMap).length === 0) {
            return;
        }

        const drawer = document.querySelector('#CartDrawer, cart-drawer, [data-cart-drawer], .cart-drawer');
        if (!drawer) return;

        let reapplyCount = 0;
        const maxReapplies = 5;
        let debounceTimer = null;
        const skuPriceMap = { ...this._lastSkuPriceMap };

        this._postCycleObserver = new MutationObserver(() => {
            if (this.isUpdatingCartUI || this._isReapplying) return;
            if (reapplyCount >= maxReapplies) {
                this._postCycleObserver.disconnect();
                this._postCycleObserver = null;
                return;
            }
            clearTimeout(debounceTimer);
            debounceTimer = setTimeout(async () => {
                reapplyCount++;
                this._isReapplying = true;
                try {
                    await this.applyDynamicPricesAfterRefresh(skuPriceMap);
                    // this.finishProcessing(); // Removed this to stop the spinner from being removed for in-flights
                } finally {
                    this._isReapplying = false;
                }
            }, 150);
        });

        this._postCycleObserver.observe(drawer, { childList: true, subtree: true });

        setTimeout(() => {
            if (this._postCycleObserver) {
                this._postCycleObserver.disconnect();
                this._postCycleObserver = null;
            }
        }, 5000);
    }

    _watchForPostEmptyRerenders() {
        const clearSubtotals = () => {
            document.querySelectorAll('.totals__subtotal-value').forEach(el => {
                const text = el.textContent.trim();
                if (text !== '-' && text !== '' && text !== '$0.00') {
                    el.textContent = ' - ';
                }
            });
        };

        clearSubtotals();

        let clearCount = 0;
        const maxClears = 10;
        let debounceTimer = null;

        const observer = new MutationObserver(() => {
            if (clearCount >= maxClears) {
                observer.disconnect();
                return;
            }
            clearTimeout(debounceTimer);
            debounceTimer = setTimeout(() => {
                clearCount++;
                clearSubtotals();
            }, 100);
        });

        observer.observe(document.body, { childList: true, subtree: true });

        setTimeout(() => {
            observer.disconnect();
            clearSubtotals();
        }, 3000);
    }

    // ─── API Response Parsing ────────────────────────────────────────

    extractPricesFromApiResponse(apiResponse) {
        const skuPriceMap = {};
        try {
            const metafields = apiResponse?.data?.Response?.data?.cart?.metafields || apiResponse?.data?.Response?.data?.cartMetafieldsSet?.metafields;

            if (!metafields?.length) return skuPriceMap;

            const valueStr = metafields[0]?.value;
            if (!valueStr) return skuPriceMap;

            const parsed = JSON.parse(valueStr);
            for (const groupKey of Object.keys(parsed)) {
                const skuPrices = parsed[groupKey];
                if (typeof skuPrices === 'object' && skuPrices !== null) {
                    for (const [sku, price] of Object.entries(skuPrices)) {
                        skuPriceMap[sku] = price;
                    }
                }
            }
        } catch (e) {
            console.error('[DP] extractPricesFromApiResponse error:', e);
        }
        return skuPriceMap;
    }

    // ─── API Response Validation ────────────────────────────────────

    _validateApiResponseOverlap(skuPriceMap, cart) {
        if (!cart?.items?.length) return false;

        const cartSkus = cart.items.map(item => item.sku).filter(Boolean);
        const responseSkus = Object.keys(skuPriceMap);

        if (responseSkus.length === 0) return false;

        const hasOverlap = cartSkus.some(sku => responseSkus.includes(sku));

        // if (!hasOverlap) {
        //     console.warn(`[DP] No SKU overlap — cart SKUs: [${cartSkus.join(', ')}], response SKUs: [${responseSkus.join(', ')}]`);
        // }

        return hasOverlap;
    }

    // ─── Apply Dynamic Prices to DOM ─────────────────────────────────

    async applyDynamicPricesAfterRefresh(skuPriceMap) {
        this.invalidateCartCache();
        const freshCart = await this.getCart();
        if (!freshCart?.items?.length) return;

        let subtotal = 0;
        let appliedAny = false;

        for (const item of freshCart.items) {
            const sku = item.sku;
            const dynamicPrice = skuPriceMap[sku];

            if (dynamicPrice != null) {
                const price = parseFloat(dynamicPrice);
                const lineTotal = price * item.quantity;
                subtotal += lineTotal;
                this.updateLineItemPriceInDOM(item.variant_id, price, lineTotal);
                appliedAny = true;
            }
        }

        if (appliedAny) {
            this.updateSubtotalDisplay(subtotal);
        }
    }

    // ─── DOM Price Updates ───────────────────────────────────────────

    updateLineItemPriceInDOM(variantId, unitPrice, lineTotal) {
        const formattedUnitPrice = this.formatMoney(unitPrice * 100);
        const formattedLineTotal = this.formatMoney(lineTotal * 100);

        const el = this.findLineItemEl(variantId);
        if (!el) return;

        const lineTotalEl = el.querySelector('.lines-gt-price');
        const unitPriceContainer = el.querySelector('.cart-item__prices .price');

        el.classList.remove('dp-cart-loading', 'dp-cart-error');
        el.classList.add('dp-cart-priced');

        if (lineTotalEl) {
            lineTotalEl.textContent = formattedLineTotal;
        }

        if (unitPriceContainer) {
            const salePrice = unitPriceContainer.querySelector('.price__sale');
            if (salePrice) salePrice.style.display = 'none';

            const regularPrice = unitPriceContainer.querySelector('.price__regular');
            if (regularPrice) {
                regularPrice.textContent = formattedUnitPrice;
            } else {
                let found = false;
                unitPriceContainer.childNodes.forEach(node => {
                    if (node.nodeType === Node.TEXT_NODE && node.textContent.trim().includes('$')) {
                        node.textContent = formattedUnitPrice;
                        found = true;
                    }
                });
                if (!found) {
                    unitPriceContainer.innerHTML = formattedUnitPrice;
                }
            }
        }
    }

    findLineItemEl(variantId) {
        return document.querySelector(`tr.cart-item[data-variant-id="${variantId}"]`) ||
            document.querySelector(`[id^="CartItem-"][data-variant-id="${variantId}"]`) ||
            document.querySelector(`[id^="CartDrawer-Item"][data-variant-id="${variantId}"]`) ||
            document.querySelector(`li.cart-item[data-variant-id="${variantId}"]`) ||
            document.querySelector(`.cart-drawer [data-variant-id="${variantId}"]`);
    }

    updateSubtotalDisplay(subtotal) {
        const formatted = this.formatMoney(subtotal * 100);
        document.querySelectorAll('.totals__subtotal-value').forEach(el => {
            el.textContent = formatted;
            el.dataset.dynamicPriceApplied = 'true';
        });
    }

    // ─── Cleanup ─────────────────────────────────────────────────────

    cleanupDynamicPricingUI() {
        document.body.classList.remove('dp-cart-processing', 'dp-prices-ready');
        document.querySelectorAll('.totals__subtotal-value').forEach(el => {
            el.removeAttribute('data-dynamic-price-applied');
            el.textContent = ' - ';
        });
        document.querySelectorAll('.dp-cart-priced, .dp-cart-loading, .dp-cart-error').forEach(el => {
            el.classList.remove('dp-cart-priced', 'dp-cart-loading', 'dp-cart-error');
        });
        this._lastSkuPriceMap = null;
        if (this._postCycleObserver) {
            this._postCycleObserver.disconnect();
            this._postCycleObserver = null;
        }
        this.enableCheckoutButtons();
    }

    // ─── Theme Cart Refresh ──────────────────────────────────────────

    getCartSections(sections) {
        return fetch(`/cart?sections=${sections.join(',')}`).then(r => r.json());
    }

    async triggerThemeCartRefresh() {
        this.isRefreshing = true;

        // Ensure suppression is active before the theme reacts
        this._setSuppression(3000);

        try {
            let sectionsToBundle = [];
            document.documentElement.dispatchEvent(
                new CustomEvent('cart:grouped-sections', { bubbles: true, detail: { sections: sectionsToBundle } })
            );

            this.invalidateCartCache();
            const fullCart = await this.getCart();

            let sections = {};
            if (sectionsToBundle.length > 0) {
                try {
                    sections = await this.getCartSections(sectionsToBundle);
                } catch (e) {
                    console.warn('DP: Failed to fetch cart sections', e);
                }
            }

            const parsedState = { ...fullCart, sections };

            document.dispatchEvent(new CustomEvent('cart:refresh', {
                detail: { open: false, source: 'dynamic-pricing' },
            }));

            window.FoxTheme.pubsub.publish(
                window.FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate, {
                    cart: parsedState,
                    source: 'dynamic-pricing',
                    name: 'dynamic-pricing',
                }
            );

            // Disable immediately after publish triggers re-render
            this.disableCheckoutButtons();

            await new Promise(resolve => setTimeout(resolve, 300));

            this.disableCheckoutButtons();
        } catch (e) {
            console.error('triggerThemeCartRefresh Error:', e);
        } finally {
            this.isRefreshing = false;
        }
        // this.enableCheckoutButtons();
    }

    pollCartUpdates() {
        return setInterval(() => {
            this.triggerThemeCartRefresh();
        }, 2000);
    }

    // ─── Cart Pricing API ────────────────────────────────────────────

    async callCartPricingAPI(cartToken, customerId) {
        if (this.pendingCartApiCall) {
            return this.pendingCartApiCall;
        }

        this.pendingCartApiCall = (async () => {
            const response = await fetch(this.cartApiConfig.url, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'pipe_id': this.cartApiConfig.pipe_id
                },
                body: JSON.stringify({ cartToken, customerId })
            });
            if (!response.ok) throw new Error(`Cart pricing API failed: ${response.status}`);
            const data = await response.json();

            if (data?.errors?.length || data?.status == false) {
                throw new Error(`Cart pricing API error: ${data.message || data.errors[0].message}`);
            }

            return data;
        })();

        try {
            return await this.pendingCartApiCall;
        } finally {
            this.pendingCartApiCall = null;
        }
    }

    // ─── Checkout Button Control ─────────────────────────────────────

    disableCheckoutButtons(silent = false) {
        // if (this.checkoutButtonsDisabled && !silent) return;

        document.querySelectorAll(
            '[name="checkout"], .cart__checkout-button, .cart-drawer__checkout, #checkout, [data-checkout-button], .shopify-payment-button button'
        ).forEach(btn => {
            btn.disabled = true;
            btn.dataset.dpDisabled = 'true';
            if (!btn.dataset.originalText) btn.dataset.originalText = btn.textContent || btn.value;
            if (!silent) btn.textContent = 'Loading prices...';
        });

        this.checkoutButtonsDisabled = true;
    }

    enableCheckoutButtons() {


        if (!this.checkoutButtonsDisabled) return;

        document.querySelectorAll('[name="checkout"]').forEach(btn => {
            btn.disabled = false;
            btn.dataset.dpDisabled = 'false';
            if (btn.dataset.originalText) btn.textContent = btn.dataset.originalText;
        });

        document.querySelectorAll('[data-dp-disabled="true"]').forEach(btn => {
            btn.disabled = false;
            btn.dataset.dpDisabled = 'false';
            if (btn.dataset.originalText) btn.textContent = btn.dataset.originalText;
        });

        this.checkoutButtonsDisabled = false;
    }

    // ─── Observers & Event Listeners ─────────────────────────────────

    setupCartPageObserver() {
        const observer = new MutationObserver((mutations) => {
            if (this._isSuppressed()) return;

            let shouldUpdate = false;

            for (const mutation of mutations) {
                if (mutation.type === 'childList' && mutation.addedNodes.length > 0) {
                    for (const node of mutation.addedNodes) {
                        if (node.nodeType === Node.ELEMENT_NODE &&
                            (node.querySelector?.('[data-cart-item], .cart-item') ||
                                node.matches?.('[data-cart-item], .cart-item'))) {
                            shouldUpdate = true;
                            break;
                        }
                    }
                }
            }

            if (shouldUpdate) {
                this.invalidateCartCache();
                this.startProcessing();
                this.debouncedProcessCart();
            }
        });

        observer.observe(document.body, { childList: true, subtree: true });

        ['cart:updated', 'cart:refresh'].forEach(e => {
            document.addEventListener(e, (event) => {
                if (this._isSuppressed()) return;
                if (event.detail?.source === 'dynamic-pricing') return;
                this.invalidateCartCache();
                this.startProcessing();
                this.debouncedProcessCart(true);
            });
        });
    }

    debouncedProcessCart(force = false) {
        clearTimeout(this.cartUIUpdateDebounceTimer);
        const delay = this._inflightAdds > 0 ? 600 : 100;
        this.cartUIUpdateDebounceTimer = setTimeout(() => this.processCartWithDynamicPricing(force), delay);
    }

    setupEventListeners() {
        document.addEventListener('change', (e) => {
            if (e.target.matches('.quantity__input, input[name="quantity"]')) {
                this.invalidateCartCache();
                this.startProcessing();
            }
        });
    }

    // ─── Fetch Interceptor ───────────────────────────────────────────

    interceptFetchAddToCart() {
        if (window.fetch.__dynamicPricingIntercepted) return;

        const originalFetch = window.fetch;
        const self = this;

        window.fetch = function(...args) {
            const [url, options] = args;
            const urlStr = typeof url === 'string' ? url : '';

            if (urlStr.includes('/cart/add') && options?.body) {
                return (async () => {
                    self._inflightAdds++;
                    try {
                        self.startProcessing();
                        const response = await originalFetch.apply(this, args);
                        if (response.ok) {
                            self.invalidateCartCache();
                            self._pendingReprocessCount = 0;
                            self.debouncedProcessCart(true);
                        }
                        return response;
                    } finally {
                        self._inflightAdds--;
                    }
                })();
            }

            if (urlStr.includes('/cart/change')) {
                return originalFetch.apply(this, args).then(async response => {
                    self.startProcessing();
                    if (response.ok) {
                        self.invalidateCartCache();
                        try {
                            const cloned = response.clone();
                            const data = await cloned.json();
                            if (!data.items || data.items.length === 0) {
                                self.cleanupDynamicPricingUI();
                                self._watchForPostEmptyRerenders();
                            }
                        } catch (e) {}
                    }
                    return response;
                });
            }

            if (urlStr.includes('/cart/update')) {
                return originalFetch.apply(this, args);
            }

            if (urlStr.includes('/cart/clear')) {
                return originalFetch.apply(this, args).then(response => {
                    if (response.ok) {
                        self.invalidateCartCache();
                        self.cleanupDynamicPricingUI();
                    }
                    return response;
                });
            }

            return originalFetch.apply(this, args);
        };

        window.fetch.__dynamicPricingIntercepted = true;
    }

    // ─── Utilities ───────────────────────────────────────────────────

    async checkForCartTokenChange() {
        try {
            const cart = await this.getCart();
            const currentToken = cart?.token;
            if (!currentToken) {
                this.reset();
                return;
            }
            const storedToken = sessionStorage.getItem(this.cartTokenStorageKey);
            if (storedToken && storedToken !== currentToken) {
                this.reset();
            }
            sessionStorage.setItem(this.cartTokenStorageKey, currentToken);
        } catch (e) {}
    }

    buildVariantSkuMap() {
        document.querySelectorAll('[data-product-json]').forEach(el => {
            try {
                const product = JSON.parse(el.textContent);
                (product.variants || []).forEach(v => {
                    if (v.id && v.sku) this.variantSkuMap.set(String(v.id), v.sku);
                });
            } catch (e) {}
        });
    }

    async getSkuFromVariantId(variantId) {
        const id = String(variantId);
        if (this.variantSkuMap.has(id)) return this.variantSkuMap.get(id);

        try {
            const response = await fetch(`/variants/${variantId}.json`);
            if (response.ok) {
                const data = await response.json();
                if (data.variant?.sku) {
                    this.variantSkuMap.set(id, data.variant.sku);
                    return data.variant.sku;
                }
            }
        } catch (e) {}

        return null;
    }

    async getDynamicPriceForVariant(variantId) {
        if (!variantId) return null;
        const sku = await this.getSkuFromVariantId(variantId);
        if (!sku) return null;

        const cached = this.getPricesFromCache([sku]);
        if (cached[sku] !== undefined) return cached[sku];

        const prices = await this.fetchPrices([sku]);
        if (prices[sku] !== undefined) {
            this.updateCache(prices);
            return prices[sku];
        }

        return null;
    }

    getCustomerId() {
        return this.config.customerId ||
            window.dynamicPricingConfig?.customerId ||
            window.ShopifyAnalytics?.meta?.page?.customerId ||
            document.querySelector('[data-customer-id]')?.dataset.customerId ||
            null;
    }

    shouldRunCartPricing() {
        const pathMatch = window.location.pathname.includes('/cart');
        const drawerExists = !!document.querySelector('#CartDrawer, cart-drawer, [data-cart-drawer]');
        return pathMatch || drawerExists;
    }

    // ─── Cart Data ───────────────────────────────────────────────────

    async getCart() {
        const now = Date.now();
        if (this._cartCache && (now - this._cartCacheTime) < this._cartCacheTTL) {
            return this._cartCache;
        }

        const response = await fetch('/cart.js');
        const cart = await response.json();
        this._cartCache = cart;
        this._cartCacheTime = Date.now();
        return cart;
    }

    invalidateCartCache() {
        this._cartCache = null;
        this._cartCacheTime = 0;
    }

    reset() {
        try {
            sessionStorage.removeItem(this.cacheKey);
            sessionStorage.removeItem(this.cartTokenStorageKey);
        } catch (e) {}
        this.priceCache = {};
        this.variantDataCache.clear();
        this.invalidateCartCache();
        this._lastSkuPriceMap = null;
        this._pendingReprocessCount = 0;
        document.body.classList.remove('dp-prices-ready');
    }
}
